import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import useQueryParams from 'hooks/useQueryParams';
import type { ReportSection } from '@reports/domain/value-objects';

export type UseReportSectionSyncProps = {
  sections: ReportSection[];
};

type ReportQueryParams = {
  section?: string;
};

/**
 * Hook to manage report section via URL query parameter
 *
 * Uses URL as the single source of truth for section selection. This enables:
 * - Shareable URLs with specific sections
 * - Browser back/forward navigation
 * - State persistence on page refresh
 * - Deep linking to specific sections
 *
 * @param {UseReportSectionSyncProps} props - Hook props
 * @param {ReportSection[]} props.sections - Available report sections
 * @returns {Object} Active section key and change handler
 * @returns {string} returns.activeSectionKey - Currently active section key
 * @returns {(sectionKey: string) => void} returns.handleSectionChange - Handler to change active section
 *
 * @example
 * const { activeSectionKey, handleSectionChange } = useReportSectionSync({
 *   sections: report.sections
 * });
 */
export const useReportSectionSync = ({ sections }: UseReportSectionSyncProps) => {
  const { queryParams, deleteQueryParam } = useQueryParams<ReportQueryParams>();
  const history = useHistory();
  const location = useLocation();

  // Get valid section keys
  const validSectionKeys = useMemo(() => sections.map((section) => section.key), [sections]);

  // Get default section key (first section)
  const defaultSectionKey = useMemo(() => sections[0]?.key ?? '', [sections]);

  // Read section from URL query parameter
  const activeSectionKey = useMemo(() => {
    const urlSection = queryParams.section;

    // Validate that the URL section exists in available sections
    if (urlSection && validSectionKeys.includes(urlSection)) {
      return urlSection;
    }

    // Return default if no valid section in URL
    return defaultSectionKey;
  }, [queryParams.section, validSectionKeys, defaultSectionKey]);

  // Clean up invalid section from URL
  useEffect(() => {
    const urlSection = queryParams.section;

    // If URL has a section that is not valid, remove it
    if (urlSection && !validSectionKeys.includes(urlSection)) {
      deleteQueryParam('section');
    }
  }, [queryParams.section, validSectionKeys, deleteQueryParam]);

  // Handle section change: update URL only
  const handleSectionChange = useCallback(
    (sectionKey: string) => {
      // Validate section key
      if (!validSectionKeys.includes(sectionKey)) {
        return;
      }

      // Update URL: remove param for default section (cleaner URLs), set it for others
      const urlSearchParams = new URLSearchParams(location.search);

      if (sectionKey && sectionKey !== defaultSectionKey) {
        urlSearchParams.set('section', sectionKey);
      } else {
        // Remove section param if it's the default (cleaner URLs)
        urlSearchParams.delete('section');
      }

      const newSearch = urlSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Only update if URL actually changed
      if (newUrl !== `${location.pathname}${location.search}`) {
        history.replace(newUrl);
      }
    },
    [history, location.pathname, location.search, validSectionKeys, defaultSectionKey],
  );

  return {
    activeSectionKey,
    handleSectionChange,
  };
};
